/**
 * `/portfolio` — every approved revision in the tenant, and what is published.
 *
 * @module router/portfolio/PortfolioRoute
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate, useSearchParams } from "react-router";

import { groupPortfolioProgrammes } from "@/components/portfolio/portfolio-groups";
import { PortfolioChangeManagement } from "@/components/portfolio/PortfolioChangeManagement";
import { PortfolioFilters } from "@/components/portfolio/PortfolioFilters";
import { PortfolioProgrammes } from "@/components/portfolio/PortfolioProgrammes";
import { PublicationPanel } from "@/components/portfolio/PublicationPanel";
import { useCurriculumHistory } from "@/components/portfolio/useCurriculumHistory";
import { usePortfolioFilters } from "@/components/portfolio/usePortfolioFilters";
import { usePublication } from "@/components/portfolio/usePublication";
import {
  Alert,
  AsyncBoundary,
  Button,
  EmptyState,
  LinkButton,
  PageHeader,
  Panel,
  useAsync,
} from "@/components/ui";
import { Bone, FormPanelBones, ListBones } from "@/components/ui/SkeletonParts";
import type { PortfolioProgrammeRevision } from "@/platform";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/**
 * Declared outside the component so React sees one type across renders.
 *
 * It takes the retry the boundary hands it: a custom renderer replaces the
 * default error card entirely, so without this the screen would offer no way
 * back from a failed load.
 */
function renderError(cause: Error, retry?: () => void): React.ReactElement {
  return (
    <Alert tone="warning" data-testid="portfolio-error">
      <span>The portfolio could not be loaded. {cause.message}</span>
      {/* A custom renderer replaces the default card, so the way back has to be
          drawn here. Optional to match AsyncBoundary, which does not promise a
          retry: offering a dead button would be worse than offering none. */}
      {retry && (
        <Button size="sm" icon="arrow-clockwise" onClick={retry} data-testid="portfolio-retry">
          Try again
        </Button>
      )}
    </Alert>
  );
}

export function PortfolioRoute(): React.ReactElement {
  const navigate = useNavigate();
  const { client, session } = useWorkspaceOutletContext();

  const state = useAsync(
    async () =>
      Promise.all([client.getTenantPortfolio(), client.getPublicCurriculumApiAccess()]).then(
        ([revisions, access]) => ({ revisions, access }),
      ),
    [client],
  );

  // The loaded list, plus whatever publishing has changed since. Publishing
  // rewrites one revision's publication rather than reloading the portfolio,
  // which would lose the filters and the scroll position.
  const [published, setPublished] = useState<Record<string, PortfolioProgrammeRevision>>({});
  const revisions = useMemo(() => {
    const loaded = state.status === "ready" ? state.data.revisions : [];
    return loaded.map((revision) => {
      const changed = published[revision.curriculumRevisionId];
      return changed ? { ...revision, latestPublication: changed.latestPublication } : revision;
    });
  }, [published, state]);

  const canManage = session.subject.accountRole === "client-admin";
  // Both, not either: the API reports `canPublish` for "the caller is a client
  // admin" and `enabled` for the tenant's entitlement, and publishing needs
  // the two together. Reading `canPublish` alone sent an unentitled tenant
  // into a preview the server refuses, and left the panel's own "not enabled"
  // notice unreachable, since it renders on `!canPublish`.
  const access = state.status === "ready" ? state.data.access : undefined;
  const canPublish = Boolean(access?.canPublish && access.enabled);

  const onRevisionChanged = useCallback((revision: PortfolioProgrammeRevision) => {
    setPublished((current) => ({ ...current, [revision.curriculumRevisionId]: revision }));
  }, []);

  // A retry fetches the authoritative list, so the local overlay is discarded
  // with it. Keeping it would re-apply a publication the server may no longer
  // report, which is worse than the failure the retry is recovering from.
  const retry = useCallback(() => {
    setPublished({});
    state.reload().catch(() => undefined);
  }, [state]);

  const filters = usePortfolioFilters(revisions);
  const publication = usePublication({ client, canPublish, onRevisionChanged });
  const history = useCurriculumHistory(client, session);
  // The programme in view is the address, so a reload and Back keep it.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("programme") ?? undefined;
  const openHistory = history.open;
  useEffect(() => {
    if (selectedId) {
      openHistory(selectedId);
    }
  }, [selectedId, openHistory]);
  // Grouped from both lists: a programme appears when any of its rows match,
  // and its history then shows every revision it has rather than only the
  // ones that survived the filters.
  // Programmes, not rows: the portfolio emits one row per applicability scope,
  // so counting rows reported three of three above a single grouped row.
  const programmeTotal = useMemo(
    () => new Set(revisions.map((revision) => revision.programmeId)).size,
    [revisions],
  );
  const groups = useMemo(
    () => groupPortfolioProgrammes(revisions, filters.filtered),
    [revisions, filters.filtered],
  );
  const openCompare = useCallback(
    (programmeId: string, revisionId: string) => {
      navigate(`/portfolio/programmes/${programmeId}/compare?to=${revisionId}`);
    },
    [navigate],
  );
  const openChangeCase = useCallback(
    (programmeId: string, changeCaseId: string) => {
      navigate(`/programmes/${programmeId}/changes/${changeCaseId}/overview/identity`);
    },
    [navigate],
  );
  const panelProgrammeId = publication.panel?.revision.programmeId;

  return (
    <div className="flex flex-col gap-6" data-testid="portfolio-workspace">
      <PageHeader
        title="Portfolio"
        description="Approved programme revisions across your organisation. Working drafts stay with their teams."
      />

      <AsyncBoundary
        state={state}
        loadingLabel="Loading the portfolio…"
        error={renderError}
        onRetry={retry}
        empty={null}
        loading={
          <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)] [&>*]:min-w-0">
            {/* The programme list beside the open revision. */}
            <Panel flush className="p-3" data-testid="portfolio-loading-master">
              <Bone className="mb-2 h-9 w-full" />
              <ListBones rows={6} />
            </Panel>
            <div className="max-lg:hidden" data-testid="portfolio-loading-detail">
              <FormPanelBones />
            </div>
          </div>
        }
        data-testid="portfolio-loading"
      >
        {() =>
          programmeTotal === 0 ? (
            // Nothing approved yet: no filters, no headers, only the next step.
            <EmptyState
              icon="archive"
              title="No programmes have been approved yet"
              description="Programmes appear here once they are approved."
              action={
                <LinkButton to="/programmes" variant="primary" icon="graduation-cap">
                  Go to programmes
                </LinkButton>
              }
              data-testid="portfolio-route-empty"
            />
          ) : (
            <>
              {publication.panel && (
                <PublicationPanel
                  publication={publication}
                  canPublish={canPublish}
                  changeManagement={
                    canManage && panelProgrammeId ? (
                      <div className="mt-4">
                        <PortfolioChangeManagement
                          client={client}
                          programmeId={panelProgrammeId}
                          onWorkspaceOpen={(changeCaseId) => {
                            navigate(
                              `/programmes/${panelProgrammeId}/changes/${changeCaseId}/overview/identity`,
                            );
                          }}
                        />
                      </div>
                    ) : undefined
                  }
                />
              )}

              <PortfolioProgrammes
                groups={groups}
                filtered={filters.active}
                filters={
                  <PortfolioFilters
                    model={filters}
                    shown={groups.length}
                    total={programmeTotal}
                    variant="flush"
                  />
                }
                canManage={canManage}
                canPublish={canPublish}
                busy={publication.busy}
                selectedId={selectedId}
                onSelect={(programmeId, options) => {
                  setParams({ programme: programmeId }, options);
                }}
                onBack={() => {
                  setParams({});
                }}
                historyState={history.stateFor}
                onRetryHistory={history.retry}
                onManage={publication.open}
                onCompare={openCompare}
                onChangeCaseOpen={openChangeCase}
              />
            </>
          )
        }
      </AsyncBoundary>
    </div>
  );
}
